<template>
	<div class="lp-welcome" v-test="'project-share-welcome'">
		<div class="w-dim" @click="close"></div>
		<div class="w-card" ref="cardRef" role="dialog" aria-modal="true" aria-labelledby="lp-welcome-title" tabindex="-1">
			<button
				class="w-x"
				type="button"
				aria-label="Close"
				v-test="'project-share-welcome-close'"
				@click="close"
				v-html="shareIcons.x"></button>
			<div class="w-head">
				<h2 id="lp-welcome-title">Welcome to {{ projectName || "your project" }}</h2>
				<p>Here's a quick tour of everything you can do in this project.</p>
			</div>
			<div class="w-video" :class="{ playing }" v-test="'project-share-welcome-video'" @click="play">
				<iframe
					v-if="playing && isEmbed"
					:src="embedSrc"
					class="w-realvideo"
					frameborder="0"
					allow="autoplay; fullscreen; picture-in-picture"
					allowfullscreen></iframe>
				<video v-else-if="playing && videoUrl" :src="videoUrl" controls autoplay playsinline class="w-realvideo"></video>
				<div v-else class="w-poster">
					<span class="w-play" v-html="shareIcons.play"></span>
				</div>
				<div class="w-vmeta">
					<span>Getting around a project</span>
					<span v-if="videoDuration">{{ videoDuration }}</span>
				</div>
			</div>
			<ul class="w-points">
				<li v-for="point in points" :key="point.text"><span class="w-ico" v-html="point.icon"></span>{{ point.text }}</li>
			</ul>
			<p class="w-again">
				Want to watch it again later? You'll find it any time under
				<RouterLink :to="{ name: 'videos' }" class="text-primary font-weight-bold" @click="close">Coaching Videos</RouterLink>.
			</p>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { ref, computed, onMounted, onBeforeUnmount } from "vue";
	import { RouterLink } from "vue-router";
	import { shareIcons } from "./shareIcons";

	// Code-review #17: this is a hand-rolled overlay (not the shared Dialog.vue), so wire the modal
	// a11y basics the shared component would provide — Escape-to-close and focus-on-open. role/aria-modal
	// live on the card template. A full focus trap is a follow-up.
	const cardRef = ref<HTMLElement | null>(null);
	function onKeydown(event: KeyboardEvent) {
		if (event.key === "Escape") {
			event.stopPropagation();
			close();
		}
	}
	onMounted(() => {
		window.addEventListener("keydown", onKeydown);
		cardRef.value?.focus();
	});
	onBeforeUnmount(() => window.removeEventListener("keydown", onKeydown));

	const props = defineProps<{
		videoUrl?: string;
		// Shown on the poster; omitted when the video's length isn't known (e.g. an env override).
		videoDuration?: string;
		projectName?: string;
	}>();

	// videoUrl may be a direct file (played in a <video>) OR a provider embed URL
	// (Vimeo/YouTube player) which must render in an <iframe>. Detect the embed case.
	const isEmbed = computed(() => !!props.videoUrl && /player\.vimeo\.com|youtube(-nocookie)?\.com\/embed/.test(props.videoUrl));
	const embedSrc = computed(() => {
		if (!props.videoUrl) return "";
		const separator = props.videoUrl.includes("?") ? "&" : "?";
		return `${props.videoUrl}${separator}autoplay=1`;
	});

	const emit = defineEmits<{
		close: [];
	}>();

	const playing = ref(false);

	const points = [
		{ icon: shareIcons.plusS, text: "Add photos, videos, notes and files from any device." },
		{ icon: shareIcons.check2, text: "Work through checklists and sheets — progress saves as you go." },
		{ icon: shareIcons.chat, text: "Leave comments so the whole team stays in the loop." }
	];

	function play() {
		playing.value = true;
	}

	function close() {
		emit("close");
	}
</script>

<style scoped>
	/* Ported verbatim from design-ref/lp-real.css (.lp-welcome / .w-*). */
	.lp-welcome {
		--blue: #2563eb;
		--ink: #111827;
		--ink2: #374151;
		--ink3: #6b7280;
		--ink4: #9ca3af;
		--line: #d5dae2;
		position: fixed;
		inset: 0;
		/* LSP-17773 (review): the overlay is teleported to body for the RECIPIENT view — what it
		   must clear is App.vue's chrome (header/banners stack at 1080–1100), not the share
		   dialog. At the previous 460 a header banner covered the top of the centered card and
		   cropped the close button (worst on short mobile viewports where the card reaches the
		   top); 1200 sits above the chrome while staying below vue-toastification (9999). */
		z-index: 1200;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 20px;
		/* Notched phones: keep the card (and its close button) inside the safe area. */
		padding-top: max(20px, env(safe-area-inset-top));
		padding-bottom: max(20px, env(safe-area-inset-bottom));
		/* Review: left/right too — the same cropping class reappears in LANDSCAPE on a notched
		   device if only the vertical insets are honored. */
		padding-left: max(20px, env(safe-area-inset-left));
		padding-right: max(20px, env(safe-area-inset-right));
		font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif;
	}
	.w-dim {
		position: absolute;
		inset: 0;
		background: rgba(15, 23, 42, 0.55);
	}
	.w-card {
		position: relative;
		z-index: 1;
		width: 720px;
		max-width: 94%;
		/* LSP-17773 (review): the cap must mirror the wrapper's per-side padding EXACTLY
		   (max(20px, safe-area inset)) — a flat 40px under-reserves on notched devices, letting
		   the card overflow the centered wrapper and crop the close button again. Review round 2:
		   the FALLBACK line must be inset-aware too — browserslist supports Safari >= 15, and
		   15.0–15.3 lack dvh entirely (env()/max() are fine since 11.1), so an insets-only-on-dvh
		   split silently reverted the fix exactly there. dvh (15.4+) additionally corrects the
		   collapsed-URL-bar over-measurement of vh. */
		max-height: calc(100vh - max(20px, env(safe-area-inset-top, 0px)) - max(20px, env(safe-area-inset-bottom, 0px)));
		max-height: calc(100dvh - max(20px, env(safe-area-inset-top, 0px)) - max(20px, env(safe-area-inset-bottom, 0px)));
		overflow-y: auto;
		background: #fff;
		border-radius: 18px;
		padding: 28px 30px 24px;
		box-shadow: 0 30px 70px -20px rgba(0, 0, 0, 0.45);
		color: var(--ink);
	}
	.w-x {
		position: sticky;
		float: right;
		top: 0;
		margin: -6px -8px 0 0;
		width: 34px;
		height: 34px;
		border: 0;
		border-radius: 999px;
		background: #fff;
		color: var(--ink3);
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 2;
	}
	.w-x:hover {
		background: #f1f5f9;
		color: var(--ink);
	}
	.w-head h2 {
		margin: 0 0 6px;
		font-size: 21px;
		font-weight: 700;
		letter-spacing: -0.01em;
		padding-right: 34px;
	}
	.w-head p {
		margin: 0 0 18px;
		font-size: 13.5px;
		color: var(--ink2);
		line-height: 1.5;
	}
	.w-video {
		border-radius: 12px;
		overflow: hidden;
		border: 1px solid var(--line);
		cursor: pointer;
		background: #0f172a;
	}
	.w-poster {
		aspect-ratio: 16 / 9;
		max-height: 56vh;
		background: radial-gradient(120% 120% at 20% 10%, #1e293b, #0b1220);
		display: flex;
		align-items: center;
		justify-content: center;
	}
	.w-realvideo {
		width: 100%;
		aspect-ratio: 16 / 9;
		max-height: 56vh;
		display: block;
		background: #000;
	}
	.w-play {
		width: 60px;
		height: 60px;
		border-radius: 999px;
		background: rgba(255, 255, 255, 0.94);
		display: flex;
		align-items: center;
		justify-content: center;
		transition: transform 0.15s ease;
	}
	.w-play :deep(svg) {
		width: 22px;
		height: 22px;
		margin-left: 3px;
		color: #0f172a;
	}
	.w-video:hover .w-play {
		transform: scale(1.07);
	}
	.w-video.playing .w-play {
		opacity: 0.35;
	}
	.w-vmeta {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 9px 12px;
		font-size: 12.5px;
		font-weight: 600;
		color: #cbd5e1;
		background: #0f172a;
	}
	.w-points {
		list-style: none;
		margin: 18px 0 20px;
		padding: 0;
		display: flex;
		flex-direction: column;
		gap: 11px;
	}
	.w-points li {
		display: flex;
		align-items: flex-start;
		gap: 10px;
		font-size: 13.5px;
		color: var(--ink2);
		line-height: 1.45;
	}
	.w-ico {
		flex-shrink: 0;
		width: 24px;
		height: 24px;
		border-radius: 999px;
		background: #eef4ff;
		color: var(--blue);
		display: flex;
		align-items: center;
		justify-content: center;
	}
	.w-ico :deep(svg) {
		width: 14px;
		height: 14px;
	}
	.w-again {
		margin: 0 0 4px;
		font-size: 12.5px;
		color: var(--ink3);
		line-height: 1.5;
	}
</style>
